<template>
  <div class="content-block">
    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid"
      class="dx-card wide-card grid-tramites">
      <DxColumn data-field="CodigoSecuencial" :caption="$t('tramites.grid_energia.id')" format="0000000000"></DxColumn>
      <DxColumn data-field="FechaAlta" :caption="$t('tramites.grid_energia.fecha_de_venta')" data-type="date"
        format="dd/MM/yyyy"></DxColumn>
      <DxColumn v-if="puedoVerAgente" data-field="NombreAgente" :caption="$t('tramites.grid.agente')">
      </DxColumn>
      <DxColumn data-field="NombreEmpresaServicios" :caption="$t('tramites.grid.comercializadora')"></DxColumn>
      <DxColumn data-field="DocumentoTitular" :caption="$t('tramites.grid.documento_titular')"></DxColumn>
      <DxColumn data-field="CodigoEstado" :caption="$t('tramites.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TieneServiciosAdicionales" :caption="$t('tramites.grid.tiene_servicios_adicionales')"
        cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TipoSuministro" :caption="$t('tramites.grid_energia.tipo_suministro')">
        <DxLookup column-auto-width="true" v-bind="getLookup('operaciones', 'tipo_suministro')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Cups" :caption="$t('tramites.grid_energia.cups')"></DxColumn>
      <DxColumn data-field="TarifaAcceso" :caption="$t('tramites.grid_energia.tarifa_de_acceso')"></DxColumn>
      <DxColumn v-if="puedoVerCoste" data-field="Comision" :caption="$t('tramites.grid.comision')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn v-if="puedoVerCoste" data-field="Retrocomision" :caption="$t('tramites.grid_energia.retrocomision')"
        data-type="number" :calculate-cell-value="formatRetrocomision" :calculate-filter-expression="getRetrocomisionFilter">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="FechaActivacion" :caption="$t('tramites.grid.fecha_alta')" data-type="date"
        format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="FechaBaja" :caption="$t('tramites.grid_energia.fecha_baja')" data-type="date" format="dd/MM/yyyy">
      </DxColumn>
      <DxColumn data-field="FechaModificacion" :caption="$t('tramites.grid.fecha_ultima_modificacion')" data-type="date"
        format="dd/MM/yyyy"></DxColumn>
      <DxColumn type="buttons" :caption="$t('tramites.grid.acciones')" :fixed="true"
        fixed-position="right" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-comment-dots text-success',
            hint: $t('operaciones.grid.ir_a_chat'),
            onClick: (e: any) => irAChat(e)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-magnifying-glass text-success',
            hint: $t('tramites.grid.ver_detalle'),
            onClick: (e: any) => verDetalle(e)
          }
        ]"/>
      <DxColumnFixing :enabled="true" />

      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `estado-operacion ${data.value.replace(' ', '')}`,
        ]">
            {{ $t(`operaciones.estado.${data.value}`) }}
        </div>
      </template>
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
      <template #masterDetailTemplate="{ data: tramite }">
        <TramitesEnergiaGridDetail :template-data="tramite" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import {
  DxColumn,
  DxDataGrid,
  DxFormat,
  DxLookup,
  DxMasterDetail,
  DxColumnFixing,
} from "devextreme-vue/data-grid";
import {
  currencyFormat,
  getDefaultGridProperties,
  getLookup,
  getRetrocomisionFilter,
  getColumnLookupBoolean
} from "@helpers/grid-helper";
import { useModalService } from "@services/modal-service";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import type CustomStore from "devextreme/data/custom_store";
import { computed, onMounted, ref } from "vue";
import TramitesEnergiaGridDetail from "@/components/tramites/TramitesEnergiaGridDetail.vue";
import OperacionesResumen from "@/components/operaciones-energia/OperacionesEnergiaResumen.vue";
import type DevExpress from "devextreme";
import { useIntl } from "@/.shared/services/i18n/i18nservice";
import { OperacionEnergia } from "@/components/nueva-venta/models/OperacionEnergia";
import { useAuthorization } from "@/.shared/services/auth/authorization";
import { PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const modal = useModalService();
const intl = useIntl();
const autho = useAuthorization();

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "IdTramite",
    load: await api.toStandardApiFunction("getTramitesEnergiaForGrid", { showLoading: false })
  });
});

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  let title = `${intl.i18n.t("tramites.detalle.titulo")} ${intl.i18n.t(`operaciones.tipo_suministro.${data.row?.data.TipoSuministro}`)} 
              ${intl.i18n.t("tramites.detalle.titulo_2")} ${data.row?.data.CodigoSecuencial.toString().padStart(10, '0')}`;

  var response = await api.callStandardApi("getOperacionEnergiaDetail", { showLoading: true }, data.row?.data.IdOperacion);
  var model = new OperacionEnergia();
  model.createFromOperacionDetail(response);

  await modal.showModal(OperacionesResumen, { data: model, tramite: data.row?.data.TipoSuministro, idTramite: data.row?.data.IdTramite }, { title: title });
}

const puedoVerCoste = computed(() => {
  return autho.hasPermission(PermissionsApp.EditAllOperacionesEnergia) || autho.hasPermission(PermissionsApp.ViewDatosEconomicosOperaciones);
})

const puedoVerAgente = computed(() => {
  return autho.hasPermission(PermissionsApp.EditAllOperacionesEnergia) || autho.hasPermission(PermissionsApp.ViewSameCompanyOperaciones) ||
    autho.hasPermission(PermissionsApp.ViewSameDelegacionOperaciones);
})

async function irAChat(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  let rutaColaboradores = import.meta.env.VITE_BASE_URL;
  let url = `${rutaColaboradores}/comunicaciones-operacion/${data.row?.data.IdOperacion}/${data.row?.data.CodigoSecuencial.toString().padStart(10, '0')}/${data.row?.data.TipoSuministro}`;
  window.open(url, '_blank');
}

const formatRetrocomision = (rowData: any) => {
  return rowData.Retrocomision ? -Math.abs(rowData.Retrocomision) : rowData.Retrocomision;
};

</script>

<style lang="scss" scoped>


.lupa, .chat {
  color: var(--text-secondary-default);
}

.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}
</style>
<style>
.grid-tramites .dx-datagrid {
  max-height: calc(100vh - 250px);
}
</style>
